import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button, showErrorToast } from '@/components';
import {
  API_KEY_SCOPES,
  ApiKeyEnvironment,
  ApiKeyScope,
  createApiKey,
  CreateApiKeyResponse,
} from '@/services/api/apiKeys';
import { DeveloperAccess } from '@/config/app';
import { extractApiErrorMessage } from '@/utils/errorHandler';

interface CreateApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  /** Which APIs the client has — the scope wording follows it. */
  access?: DeveloperAccess;
}

const ALL_APIS: DeveloperAccess = { shipments: true, orders: true };

// The outbound-order endpoints are guarded by the same shipments:* scopes,
// so only the wording changes per client — never the scope names.
const SHIPMENT_SCOPES: Record<ApiKeyScope, string> = {
  all: 'Full access — every scope below, plus any added in the future',
  'shipments:read': 'List shipments, get detail, timeline & POD',
  'shipments:write': 'Create shipments',
  'shipments:cancel': 'Cancel shipments (before hub scan)',
};

const ORDER_SCOPES: Record<ApiKeyScope, string> = {
  all: 'Full access — every scope below, plus any added in the future',
  'shipments:read': 'List outbound orders and get their detail',
  'shipments:write': 'Create outbound orders (and step sandbox orders)',
  'shipments:cancel': 'Cancel outbound orders (before they ship)',
};

const BOTH_SCOPES: Record<ApiKeyScope, string> = {
  all: 'Full access — every scope below, plus any added in the future',
  'shipments:read': 'List & get shipments (timeline, POD) and outbound orders',
  'shipments:write': 'Create shipments and outbound orders',
  'shipments:cancel':
    'Cancel shipments (before hub scan) and outbound orders (before they ship)',
};

const scopeDescriptionsOf = (access: DeveloperAccess) =>
  access.shipments && access.orders
    ? BOTH_SCOPES
    : access.orders
      ? ORDER_SCOPES
      : SHIPMENT_SCOPES;

const sandboxBlurbOf = (access: DeveloperAccess) =>
  `For building & testing. Cannot authenticate production requests — ${
    access.shipments && access.orders
      ? 'no real shipments or orders are created'
      : access.orders
        ? 'no order reaches a warehouse'
        : 'no real shipments are created'
  }.`;

const ENV_OPTIONS: {
  value: ApiKeyEnvironment;
  label: string;
  prefix: string;
  blurb?: string;
}[] = [
  {
    value: 'TEST',
    label: 'Sandbox',
    prefix: 'dash_test_…',
  },
  {
    value: 'LIVE',
    label: 'Production',
    prefix: 'dash_live_…',
    blurb:
      'For real traffic. Cannot be used against the sandbox — keep it only in your production secret manager.',
  },
];

/**
 * Two-phase modal: the form, then — on success — the one-time key reveal.
 * The reveal is deliberately hard to dismiss by accident (no backdrop close):
 * once closed, the plaintext is gone forever (the backend stores only a hash).
 */
const CreateApiKeyModal: React.FC<CreateApiKeyModalProps> = ({
  open,
  onClose,
  onCreated,
  access = ALL_APIS,
}) => {
  const scopeDescriptions = scopeDescriptionsOf(access);
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<ApiKeyEnvironment>('TEST');
  const [scopes, setScopes] = useState<ApiKeyScope[]>(['shipments:read']);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setEnvironment('TEST');
      setScopes(['shipments:read']);
      setSubmitting(false);
      setCreated(null);
      setCopied(false);
    }
  }, [open]);

  if (!open) return null;

  const toggleScope = (s: ApiKeyScope) =>
    setScopes((prev) => {
      // 'all' is a select-all: checking it checks every scope, unchecking it
      // clears them.
      if (s === 'all') return prev.includes('all') ? [] : [...API_KEY_SCOPES];
      const next = prev.includes(s)
        ? prev.filter((x) => x !== s && x !== 'all')
        : [...prev.filter((x) => x !== 'all'), s];
      // Every specific scope checked = 'all' checks itself.
      const specifics = API_KEY_SCOPES.filter((x) => x !== 'all');
      return specifics.every((x) => next.includes(x))
        ? [...API_KEY_SCOPES]
        : next;
    });

  const submit = async () => {
    if (!name.trim() || scopes.length === 0 || submitting) return;
    setSubmitting(true);
    try {
      const res = await createApiKey({
        name: name.trim(),
        environment,
        scopes,
      });
      setCreated(res);
      onCreated();
    } catch (err) {
      showErrorToast('Failed to create API key', extractApiErrorMessage(err, 'Failed to create API key'));
    } finally {
      setSubmitting(false);
    }
  };

  const copyKey = async () => {
    if (!created) return;
    try {
      await navigator.clipboard.writeText(created.key);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard can be unavailable (permissions) — the key stays selectable.
    }
  };

  const closeReveal = () => {
    setCreated(null);
    onClose();
  };

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-40 bg-bg-strong-950/40"
        aria-hidden="true"
        // While the one-time key is on screen, a stray backdrop click must not
        // destroy it — closing is explicit only.
        onClick={created || submitting ? undefined : onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-xl bg-bg-white-0 shadow-card-lg">
          {created ? (
            /* ── Phase 2: one-time reveal ─────────────────────────── */
            <div className="px-6 py-5">
              <div className="flex items-center gap-2">
                <i className="ri-key-2-line text-lg text-success-base" />
                <h2 className="text-lg font-semibold text-text-strong-950">
                  Your new {created.environment === 'LIVE' ? 'Production' : 'Sandbox'} key
                </h2>
              </div>
              <div className="mt-4 rounded-lg bg-bg-strong-950 px-4 py-3">
                <code className="break-all font-mono text-sm text-static-white">
                  {created.key}
                </code>
              </div>
              <div className="mt-3 flex items-start gap-2 rounded-lg bg-warning-lighter px-3 py-2.5 text-xs text-warning-dark">
                <i className="ri-error-warning-line mt-px" />
                <span>
                  <b>This key is shown only once.</b> Copy it to your secret
                  manager now — Dash stores only a hash and can never display
                  it again. If it's lost, create a new key and revoke this one.
                </span>
              </div>
              <div className="mt-4 flex justify-end gap-2">
                <Button variant="white" onClick={copyKey}>
                  <i className="ri-file-copy-line mr-1.5" />
                  {copied ? 'Copied ✓' : 'Copy key'}
                </Button>
                <Button onClick={closeReveal}>I've stored it safely</Button>
              </div>
            </div>
          ) : (
            /* ── Phase 1: the form ────────────────────────────────── */
            <>
              <div className="border-b border-stroke-soft-200 px-6 py-4">
                <h2 className="text-lg font-semibold text-text-strong-950">
                  Create API key
                </h2>
                <p className="mt-0.5 text-xs text-text-sub-600">
                  Scopes are fixed after creation — for more access, create a
                  new key. Max 2 active keys per environment.
                </p>
              </div>

              <div className="space-y-5 px-6 py-5">
                {/* Name */}
                <div>
                  <label
                    htmlFor="api-key-name"
                    className="mb-1.5 block text-xs font-medium text-text-sub-600"
                  >
                    Key name
                  </label>
                  <input
                    id="api-key-name"
                    type="text"
                    maxLength={40}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. erp-production"
                    className="w-full rounded-lg border border-stroke-sub-300 bg-bg-white-0 px-3 py-2 text-sm text-text-strong-950 placeholder:text-text-soft-400 focus:border-primary focus:outline-none"
                  />
                </div>

                {/* Environment */}
                <div>
                  <span className="mb-1.5 block text-xs font-medium text-text-sub-600">
                    Environment
                  </span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {ENV_OPTIONS.map((opt) => {
                      const active = environment === opt.value;
                      return (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setEnvironment(opt.value)}
                          aria-pressed={active}
                          className={`rounded-xl border px-4 py-3 text-left transition-colors ${
                            active
                              ? 'border-primary bg-(--primary-alpha-10)'
                              : 'border-stroke-soft-200 hover:border-stroke-sub-300'
                          }`}
                        >
                          <span className="flex items-center justify-between">
                            <span className="text-sm font-semibold text-text-strong-950">
                              {opt.label}
                            </span>
                            <code className="font-mono text-[11px] text-text-soft-400">
                              {opt.prefix}
                            </code>
                          </span>
                          <span className="mt-1 block text-[11px] leading-relaxed text-text-sub-600">
                            {opt.blurb ?? sandboxBlurbOf(access)}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Scopes */}
                <div>
                  <span className="mb-1.5 block text-xs font-medium text-text-sub-600">
                    Scopes
                  </span>
                  {access.orders && !access.shipments && (
                    <p className="-mt-0.5 mb-2 text-[11px] text-text-soft-400">
                      The outbound order API uses the{' '}
                      <code className="font-mono">shipments:*</code> scopes.
                    </p>
                  )}
                  <div className="space-y-2">
                    {API_KEY_SCOPES.map((s) => (
                      <label
                        key={s}
                        className="flex cursor-pointer items-start gap-2.5 rounded-lg border border-stroke-soft-200 px-3 py-2.5 hover:border-stroke-sub-300"
                      >
                        <input
                          type="checkbox"
                          checked={scopes.includes(s)}
                          onChange={() => toggleScope(s)}
                          className="mt-0.5 accent-primary"
                        />
                        <span>
                          <code className="block font-mono text-xs font-semibold text-text-strong-950">
                            {s}
                          </code>
                          <span className="text-[11px] text-text-sub-600">
                            {scopeDescriptions[s]}
                          </span>
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-stroke-soft-200 px-6 py-4">
                <Button variant="white" onClick={onClose} disabled={submitting}>
                  Cancel
                </Button>
                <Button
                  onClick={submit}
                  disabled={!name.trim() || scopes.length === 0 || submitting}
                >
                  {submitting ? 'Creating…' : 'Create key'}
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </>,
    document.body
  );
};

export default CreateApiKeyModal;
